import React from "react";
import { GameName } from "../../../types/games";
import {
  THIS_OR_THAT,
  TRIVIA,
  PICTURE_PUZZLE,
  WORD_LINK,
  WORDSMITH,
  TYPING_TEST,
} from "../../../constants";
import {
  TriviaGameConfig,
  PicturePuzzleGameConfig,
  TypingTestGameConfig,
  WordLinkGameConfig,
  WordsmithGameConfig,
  CustomizableGameConfig,
} from "../../../types/games";

interface GameConfigSetterProps {
  gameType?: GameName;
  gameConfig?: CustomizableGameConfig;
  onChange: (config: CustomizableGameConfig) => void;
}

/**
 * GameConfigSetter component
 *
 * Renders essential configuration options specific to each game type for journeys.
 * Focused on gameplay parameters that affect the player experience, excluding Slack-specific options.
 */
export const GameConfigSetter: React.FC<GameConfigSetterProps> = ({
  gameType,
  gameConfig,
  onChange,
}) => {
  // Early return if no game type is selected
  if (!gameType) {
    return (
      <div className="text-gray-500 text-sm">
        Select a game type to configure options
      </div>
    );
  }

  // Handler for updating config values
  const handleConfigChange = (key: string, value: any) => {
    if (!gameConfig) return;
    onChange({ ...gameConfig, [key]: value });
  };

  // For checkbox changes
  const handleCheckboxChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleConfigChange(key, e.target.checked);
    };

  // For number input changes
  const handleNumberChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleConfigChange(key, parseInt(e.target.value, 10));
    };

  // Default allow_rematch checkbox that appears for all game types
  const AllowRematchOption = () => (
    <div className="flex items-center mt-2">
      <input
        id="allow_rematch"
        type="checkbox"
        checked={gameConfig?.allow_rematch === true} // Default to false if undefined
        onChange={handleCheckboxChange("allow_rematch")}
        className="focus:ring-brand h-4 w-4 text-brand border-gray-300 rounded"
      />
      <label
        htmlFor="allow_rematch"
        className="ml-3 block text-sm text-gray-700"
      >
        Allow Rematch
      </label>
    </div>
  );

  // Render different config options based on game type
  switch (gameType) {
    case TRIVIA:
      const triviaConfig = gameConfig as TriviaGameConfig;
      return (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="questions_per_game"
              className="block text-sm text-gray-700"
            >
              Questions per game
            </label>
            <input
              id="questions_per_game"
              type="number"
              min={1}
              max={20}
              value={triviaConfig?.questions_per_game || 5}
              onChange={handleNumberChange("questions_per_game")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="time_per_question"
              className="block text-sm text-gray-700"
            >
              Time per question (seconds)
            </label>
            <input
              id="time_per_question"
              type="number"
              min={10}
              max={120}
              value={triviaConfig?.time_per_question || 30}
              onChange={handleNumberChange("time_per_question")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <AllowRematchOption />
        </div>
      );

    case THIS_OR_THAT:
      return (
        <div className="space-y-3">
          <AllowRematchOption />
        </div>
      );

    case PICTURE_PUZZLE:
      const picturePuzzleConfig = gameConfig as PicturePuzzleGameConfig;

      return (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="max_guesses"
              className="block text-sm text-gray-700"
            >
              Max guesses
            </label>
            <input
              id="max_guesses"
              type="number"
              min={1}
              max={10}
              value={picturePuzzleConfig?.max_guesses || 3}
              onChange={handleNumberChange("max_guesses")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <AllowRematchOption />
        </div>
      );

    case TYPING_TEST:
      const typingTestConfig = gameConfig as TypingTestGameConfig;
      return (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="timeout_seconds"
              className="block text-sm text-gray-700"
            >
              Timeout (seconds)
            </label>
            <input
              id="timeout_seconds"
              type="number"
              min={30}
              max={300}
              value={typingTestConfig?.timeout_seconds || 60}
              onChange={handleNumberChange("timeout_seconds")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <AllowRematchOption />
        </div>
      );

    case WORD_LINK:
      const wordLinkConfig = gameConfig as WordLinkGameConfig;
      return (
        <div className="space-y-3">
          <div>
            <label htmlFor="max_turns" className="block text-sm text-gray-700">
              Max turns
            </label>
            <input
              id="max_turns"
              type="number"
              min={1}
              max={30}
              value={wordLinkConfig?.max_turns || 10}
              onChange={handleNumberChange("max_turns")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="max_strikes"
              className="block text-sm text-gray-700"
            >
              Max strikes
            </label>
            <input
              id="max_strikes"
              type="number"
              min={1}
              max={10}
              value={wordLinkConfig?.max_strikes || 3}
              onChange={handleNumberChange("max_strikes")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="min_players"
              className="block text-sm text-gray-700"
            >
              Min players
            </label>
            <input
              id="min_players"
              type="number"
              min={1}
              max={10}
              value={wordLinkConfig?.min_players || 1}
              onChange={handleNumberChange("min_players")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <AllowRematchOption />
        </div>
      );

    case WORDSMITH:
      const wordsmithConfig = gameConfig as WordsmithGameConfig;
      return (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="game_duration"
              className="block text-sm text-gray-700"
            >
              Game duration (seconds)
            </label>
            <input
              id="game_duration"
              type="number"
              min={1}
              max={60}
              value={
                typeof wordsmithConfig?.game_duration === "number"
                  ? wordsmithConfig.game_duration
                  : 15
              }
              onChange={handleNumberChange("game_duration")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="min_players_per_team"
              className="block text-sm text-gray-700"
            >
              Min players per team
            </label>
            <input
              id="min_players_per_team"
              type="number"
              min={1}
              max={10}
              value={wordsmithConfig?.min_players_per_team || 1}
              onChange={handleNumberChange("min_players_per_team")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="num_teams" className="block text-sm text-gray-700">
              Number of teams
            </label>
            <input
              id="num_teams"
              type="number"
              min={1}
              max={4}
              value={wordsmithConfig?.num_teams || 2}
              onChange={handleNumberChange("num_teams")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>

          <AllowRematchOption />
        </div>
      );

    default:
      return (
        <div className="space-y-3">
          <div className="text-gray-500 text-sm">
            No specific configuration options for this game type
          </div>
          <AllowRematchOption />
        </div>
      );
  }
};
